<template>
	<div>
		<h2 class="clearfix mb-4">
			{{$t('edit')}}
			<div class="float-right">
				<button @click="saveStage" class="btn btn-primary" :disabled="!stage.title">{{ $t('save') }}</button>
			</div>
		</h2>

		<div class="row mb-4">
			<div class="col-12 col-xl-7">

				<TextInput
					:label="$t('title')"
					v-model="stage.title"
				/>

				<TextAreaAutosize
					:label="$t('description')"
					v-model="stage.note"
					class="mb-4"
				/>

				<b-tabs pills v-model="tabIndex" content-class="mb-4 pt-2">
					<b-tab>
						<template #title>
							{{$t('forms')}}
							<span class="badge rounded-pill bg-secondary text-white" v-if="stage.forms.length">{{stage.forms.length}}</span>
						</template>
						<FormsPicker
							v-model="stage.forms"
							:loading="loading"
						/>
					</b-tab>
					<b-tab>
						<template #title>
							{{$t('tests')}}
							<span class="badge rounded-pill bg-secondary text-white" v-if="stage.tests.length">{{stage.tests.length}}</span>
						</template>
						<TestsPicker
							v-model="stage.tests"
							:loading="loading"
						/>
					</b-tab>
					<b-tab>
						<template #title>
							{{$t('learning-outcomes')}}
							<span class="badge rounded-pill bg-secondary text-white" v-if="stage.learningoutcomes.length">{{stage.learningoutcomes.length}}</span>
						</template>
						<LearningOutcomesMapper
							v-model="stage.learningoutcomes"
							:loading="loading"
						/>
					</b-tab>
				</b-tabs>

				<div class="row mb-4 d-none d-xl-block">
					<div class="col-12">
						<TagSelector v-model="stage.tags" />
					</div>
				</div>
			</div>

			<div class="col-12 col-xl-5">
				<div v-if="stage.forms.length" class="row">
					<div class="col-12 col-lg-4 col-xl-12 mb-4">
						<h2>{{ $t('subject')}}</h2>

						<YesNoToggle v-model="stage.hassubject" :label="$t('this-form-is-about-another-user')" />

						<template v-if="stage.hassubject">

							<RoleSelector v-if="stage.hassubject" v-model="stage.subjectroles" class="mb-3" :slim="true"/>

							<SelectInput
								:label="$t('visible-to-subject')"
								v-model="stage.visibletosubjectafter"
								:options="visibletosubjectoptions"
								optionValue="value"
								optionLabel="caption"
								:nullable="false"
							/>
						</template>
					</div>

					<div class="col-12 col-lg-4 col-xl-12 mb-4">
						<h2>{{ $t('review')}}</h2>

						<YesNoToggle v-model="stage.requirereview" :label="$t('require-review')" />

						<RoleSelector v-if="stage.requirereview" v-model="stage.reviewroles" class="mb-3" :slim="true"/>
					</div>

					<div class="col-12 col-lg-4 col-xl-12 mb-4">
						<h2>{{ $t('sign-off')}}</h2>

						<YesNoToggle v-model="stage.requiresignoff" :label="$t('require-signoff')" />

						<div v-if="stage.requiresignoff">
							<RoleSelector v-model="stage.signoffroles" class="mb-3" :slim="true"/>

							<div class="form-group">
								<label class="block">{{$t('allow-external-signoff')}}</label>
								<p>{{$t('form-can-signed-off-by-a-non-cafs-user')}}</p>
								<div class="btn-group block">
									<a class="btn" :class="{ 'btn-secondary': stage.allowanonymoussignoff == false, 'btn-success': stage.allowanonymoussignoff == true }" @click="stage.allowanonymoussignoff = true" :aria-label="$t('yes')">{{ $t('yes') }}</a>
									<a class="btn" :class="{ 'btn-secondary': stage.allowanonymoussignoff == true, 'btn-success': stage.allowanonymoussignoff == false }" @click="stage.allowanonymoussignoff = false" :aria-label="$t('no')">{{ $t('no') }}</a>
								</div>
							</div>

						</div>
					</div>

					<div class="col-12 col-lg-4 col-xl-12 mb-4">
						<YesNoToggle v-model="stage.updateattendance" :label="$t('form-submissions-can-be-used-as-attendance-evidence')" />
					</div>

                    <div class="col-12 col-lg-4 col-xl-12 mb-4" v-if="stage.updateattendance">
						<YesNoToggle v-model="stage.tteventmandatory" :label="$t('timetable-event-must-be-attached-to-submit-form')" />
					</div>

					<div class="col-12 col-lg-4 col-xl-12 mb-4">
						<YesNoToggle v-model="stage.caneditlearningoutcomes" :label="$t('user-can-attach-learning-outcomes-to-their-submission')" />
					</div>
				</div>
			</div>
		</div>

		<div class="row mb-4 d-block d-xl-none">
			<div class="col-12">
				<TagSelector v-model="stage.tags" />
			</div>
		</div>
    </div>
</template>

<script>
import LearningOutcomesMapper from "src/components/LearningOutcomes/LearningOutcomesMapper";
import FormsPicker from "src/components/FormResponses/FormSelector";
import TestsPicker from "src/components/CompetencyTests/TestSelector";
import RoleSelector from "src/components/Roles/RoleSelector";
import TagSelector from "src/components/Tags/GroupedTagSelector";
import YesNoToggle from "src/components/Inputs/YesNoToggle";

export default {
	components: {
		LearningOutcomesMapper,
		FormsPicker,
		TestsPicker,
		RoleSelector,
		TagSelector,
		YesNoToggle,
	},
	mounted() {
		this.fetchStage().then(() => {
			this.loading = false
		})
	},
    data() {
        return {
			loading: true,
			submitting: false,
        	tabIndex: 0,
        	stage: {
        		id: null,
				workflow_id: this.$route.params.workflowId,
        		title: '',
        		note: '',
        		hassubject: false,
        		visibletosubjectafter: 'never',
        		requirereview: false,
        		requiresignoff: false,
        		allowanonymoussignoff: false,
        		subjectroles: [],
        		reviewroles: [],
        		signoffroles: [],
        		forms: [],
        		tests: [],
        		tags: [],
        		learningoutcomes: [],
        	},
        	saved: false,
        	visibletosubjectoptions: [
        		{ value: 'never', caption: this.$t('never') },
        		{ value: 'submit', caption: this.$t('after-submission') },
        		{ value: 'review',caption: this.$t('after-review') },
        		{ value: 'signoff', caption: this.$t('after-signoff') },
        	]
        }
    },
    methods: {
    	saveStage() {
			if (this.submitting) return
			this.submitting = true

			this.$store.commit('showBlockingSpinner'
			)
			this.$cfetch(`${this.$settings.APIBaseURL}/workflows/stages/save`, {
  				method: 'post',
  				data: this.stage,
  			})
			.then(res => {
				if (res.status !== 'SUCCESS') throw new Error('Unexpected error saving workflow stage')

				this.$router.go(-1);
			})
			.catch(error => {
				this.$log.error.call(this, error);
				this.$store.commit('openDialog', {
					title: this.$t('something-went-wrong'),
					content: this.$t('error-saving-stage'),
				})
			})
			.finally(() => {
				this.$store.commit('hideBlockingSpinner')
				this.submitting = false
			})
    	},
    	fetchStage() {
			if (!this.$route.params.id) return Promise.resolve()

			this.$store.commit('showBlockingSpinner')

  			return this.$cfetch(`${this.$settings.APIBaseURL}/workflows/stages/get`, {
  				data: {
  					id: this.$route.params.id
  				}
  			})
			.then(res => {
				if (res.status !== 'SUCCESS') throw new Error('Unexpected error fetching stage')

				this.stage = res.result
			})
			.catch(error => {
				console.error(error)
				this.$log.error.call(this, error)
				this.$store.commit('openDialog', {
					title: this.$t('something-went-wrong'),
					content: this.$t('error-fetching-stage'),
					confirmCallBack: function () {
						this.$router.push('/settings/workflows')
					}
				})
			})
			.finally(() => {
				this.$store.commit('hideBlockingSpinner')
			})
    	},
    },
    computed: {
  		roleoptions() {
  			const options = [];
  			for(let role of this.$config.getValue('roles')) {
  				options.push({ text: role.name, value: role.id });
  			}
  			return options;
  		}
    },
}
</script>
